import { apiClient } from "../axiosInstance";

class AdminService {
  constructor() {
    this.baseUrl = "/admins";
  }

  // ============================================
  // PUBLIC ENDPOINTS (No authentication required)
  // ============================================

  /**
   * Login admin
   * @param {Object} credentials - { email, password }
   */
  async login(credentials) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/login`, {
        email: credentials.email,
        password: credentials.password,
      });

      return {
        success: true,
        data: response.data,
        admin: response.data?.admin || response.data?.user,
        token: response.data?.token,
        refreshToken: response.data?.refreshToken,
        expiresIn: response.data?.expiresIn
      };
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  }

  /**
   * Refresh access token
   * @param {string} refreshToken - The refresh token
   */
  async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/refresh-token`, {
        refreshToken
      });
      
      return response.data;
    } catch (error) {
      console.error("Refresh token error:", error);
      throw error;
    }
  }

  /**
   * Verify JWT token
   * @param {string} token - The token to verify
   */
  async verifyToken(token) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/verify-token`, {
        token
      });
      
      return response.data;
    } catch (error) {
      console.error("Verify token error:", error);
      throw error;
    }
  }

  // ============================================
  // PROFILE ROUTES (Authenticated admin)
  // ============================================

  /**
   * Get current admin profile
   */
  async getProfile() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/profile`);
      return response.data;
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  }

  /**
   * Update current admin profile
   * @param {Object} data - { firstName, lastName, phoneNumber, profilePictureAlt }
   * @param {File} profilePicture - Optional profile picture file
   */
  async updateProfile(data, profilePicture = null) {
    try {
      let response;
      
      if (profilePicture) {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (data[key] !== undefined && data[key] !== null) {
            formData.append(key, data[key]);
          }
        });
        formData.append('profilePicture', profilePicture);
        
        response = await apiClient.patch(`${this.baseUrl}/profile`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await apiClient.patch(`${this.baseUrl}/profile`, data);
      }
      
      return response.data;
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  }

  /**
   * Update admin profile picture
   * @param {File} profilePicture - Profile picture file
   */
  async updateProfilePicture(profilePicture) {
    try {
      const formData = new FormData();
      formData.append('profilePicture', profilePicture);
      
      const response = await apiClient.post(`${this.baseUrl}/profile/picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data;
    } catch (error) {
      console.error("Update profile picture error:", error);
      throw error;
    }
  }

  /**
   * Update admin profile picture from base64
   * @param {string} base64Data - Base64 encoded image data
   * @param {string} mimeType - Image MIME type
   * @param {string} alt - Optional alt text
   */
  async updateProfilePictureFromBase64(base64Data, mimeType, alt = null) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/profile/picture-base64`, {
        base64Data,
        mimeType,
        alt
      });
      
      return response.data;
    } catch (error) {
      console.error("Update profile picture from base64 error:", error);
      throw error;
    }
  }

  /**
   * Remove admin profile picture
   */
  async removeProfilePicture() {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/profile/picture`);
      return response.data;
    } catch (error) {
      console.error("Remove profile picture error:", error);
      throw error;
    }
  }

  /**
   * Get admin profile picture
   */
  async getProfilePicture() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/profile/picture`);
      return response.data;
    } catch (error) {
      console.error("Get profile picture error:", error);
      throw error;
    }
  }

  /**
   * Change admin password
   * @param {Object} data - { currentPassword, newPassword }
   */
  async changePassword(data) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/change-password`, data);
      return response.data;
    } catch (error) {
      console.error("Change password error:", error);
      throw error;
    }
  }

  /**
   * Logout admin (clear refresh token)
   */
  async logout() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/logout`, {});
      return response.data;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  }

  /**
   * Check if admin has specific permission
   * @param {string} permission - The permission to check
   */
  async checkPermission(permission) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/check-permission/${permission}`);
      return response.data;
    } catch (error) {
      console.error("Check permission error:", error);
      throw error;
    }
  }

  // ============================================
  // ADMIN MANAGEMENT ROUTES (Super Admin & Admin)
  // ============================================

  /**
   * Get all admins with pagination and filters
   * @param {Object} params - { page, limit, role, search, isActive, hasProfilePicture }
   */
  async getAllAdmins(params = {}) {
    try {
      const response = await apiClient.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Get all admins error:", error);
      throw error;
    }
  }

  /**
   * Get admin statistics
   */
  async getAdminStats() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Get admin stats error:", error);
      throw error;
    }
  }

  /**
   * Get recently active admins
   * @param {number} hours - Hours to look back (optional)
   */
  async getRecentlyActiveAdmins(hours = 24) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/recently-active`, {
        params: { hours }
      });
      return response.data;
    } catch (error) {
      console.error("Get recently active admins error:", error);
      throw error;
    }
  }

  /**
   * Get admins by role
   * @param {string} role - Admin role
   */
  async getAdminsByRole(role) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/role/${role}`);
      return response.data;
    } catch (error) {
      console.error("Get admins by role error:", error);
      throw error;
    }
  }

  /**
   * Get admins by multiple roles
   * @param {Array<string>} roles - Array of admin roles
   */
  async getAdminsByRoles(roles) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/roles`, { roles });
      return response.data;
    } catch (error) {
      console.error("Get admins by roles error:", error);
      throw error;
    }
  }

  /**
   * Get admin by ID
   * @param {number} id - Admin ID
   */
  async getAdminById(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Get admin by id error:", error);
      throw error;
    }
  }

  /**
   * Get admin by UUID
   * @param {string} uuid - Admin UUID
   */
  async getAdminByUuid(uuid) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/uuid/${uuid}`);
      return response.data;
    } catch (error) {
      console.error("Get admin by uuid error:", error);
      throw error;
    }
  }

  /**
   * Get admin activity timeline
   * @param {number} id - Admin ID
   */
  async getAdminActivity(id) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/${id}/activity`);
      return response.data;
    } catch (error) {
      console.error("Get admin activity error:", error);
      throw error;
    }
  }

  // ============================================
  // SUPER ADMIN ONLY ROUTES
  // ============================================

  /**
   * Register a new admin (Super Admin only)
   * @param {Object} data - { email, password, firstName, lastName, phoneNumber, role, permissions, profilePictureAlt }
   * @param {File} profilePicture - Optional profile picture file
   */
  async registerAdmin(data, profilePicture = null) {
    try {
      let response;
      
      if (profilePicture) {
        const formData = new FormData();
        Object.keys(data).forEach(key => {
          if (data[key] !== undefined && data[key] !== null) {
            if (Array.isArray(data[key])) {
              formData.append(key, JSON.stringify(data[key]));
            } else {
              formData.append(key, data[key]);
            }
          }
        });
        formData.append('profilePicture', profilePicture);
        
        response = await apiClient.post(`${this.baseUrl}/register`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await apiClient.post(`${this.baseUrl}/register`, data);
      }
      
      return {
        success: true,
        data: response.data,
        admin: response.data?.admin
      };
    } catch (error) {
      console.error("Register admin error:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Registration failed";
      throw new Error(errorMessage);
    }
  }

  /**
   * Update admin role (Super Admin only)
   * @param {number} id - Admin ID
   * @param {string} role - New role
   */
  async updateAdminRole(id, role) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/role`, { role });
      return response.data;
    } catch (error) {
      console.error("Update admin role error:", error);
      throw error;
    }
  }

  /**
   * Update admin permissions (Super Admin only)
   * @param {number} id - Admin ID
   * @param {Array} permissions - New permissions array
   */
  async updateAdminPermissions(id, permissions) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/permissions`, {
        permissions,
      });
      return response.data;
    } catch (error) {
      console.error("Update admin permissions error:", error);
      throw error;
    }
  }

  /**
   * Toggle admin active status (Super Admin only)
   * @param {number} id - Admin ID
   * @param {boolean} isActive - New status
   */
  async toggleAdminStatus(id, isActive) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`, {
        isActive,
      });
      return response.data;
    } catch (error) {
      console.error("Toggle admin status error:", error);
      throw error;
    }
  }

  /**
   * Force reset admin password (Super Admin only)
   * @param {number} id - Admin ID
   * @param {string} newPassword - New password
   */
  async forceResetPassword(id, newPassword) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/${id}/force-reset-password`, {
        newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Force reset password error:", error);
      throw error;
    }
  }

  /**
   * Soft delete admin (Super Admin only)
   * @param {number} id - Admin ID
   */
  async deleteAdmin(id) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Delete admin error:", error);
      throw error;
    }
  }

  /**
   * Bulk delete admins (Super Admin only)
   * @param {Array<number>} adminIds - Array of admin IDs
   */
  async bulkDeleteAdmins(adminIds) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/bulk-delete`, { adminIds });
      return response.data;
    } catch (error) {
      console.error("Bulk delete admins error:", error);
      throw error;
    }
  }

  /**
   * Bulk update admin status (Super Admin only)
   * @param {Array<number>} adminIds - Array of admin IDs
   * @param {boolean} isActive - New status
   */
  async bulkUpdateStatus(adminIds, isActive) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-status`, {
        adminIds,
        isActive,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update status error:", error);
      throw error;
    }
  }

  /**
   * Bulk update admin roles (Super Admin only)
   * @param {Array<number>} adminIds - Array of admin IDs
   * @param {string} role - New role
   */
  async bulkUpdateRole(adminIds, role) {
    try {
      const response = await apiClient.patch(`${this.baseUrl}/bulk-role`, {
        adminIds,
        role,
      });
      return response.data;
    } catch (error) {
      console.error("Bulk update role error:", error);
      throw error;
    }
  }
}

// Create and export a single instance
export const adminService = new AdminService();